import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import GlobalSearch from './GlobalSearch';
import ReadonlyBanner from './ReadonlyBanner';
import ReportIssueButton from './ReportIssueButton';
import MobileBottomNav from './MobileBottomNav';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { companyProfileAPI, notificationAPI } from '../services/api';
import { getToken } from '../lib/tokenStorage';
import { Moon, Sun, Building2, Menu, X, Bell, Shield } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// --- Push subscription helper ---
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.register('/sw-push.js');
    const res = await notificationAPI.vapidPublicKey();
    const pubKey = res.data?.public_key;
    if (!pubKey) return;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pubKey),
    });
    await notificationAPI.subscribe(sub.toJSON());
  } catch (e) {
    // Permission denied or not supported — silently ignore
  }
}

export default function Layout({ children }) {
  const { isDark, toggle } = useTheme();
  const { user } = useAuth();
  const [companyName, setCompanyName] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isSuperAdmin = user?.role === 'super_admin';

  const sseRef = useRef(null);

  const refreshUnread = useCallback(() => {
    notificationAPI.unreadCount()
      .then(r => setUnreadCount(r.data?.unread_count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      if (user.company?.name) {
        setCompanyName(user.company.name);
      } else {
        companyProfileAPI.get()
          .then(r => setCompanyName(r.data?.name || ''))
          .catch(() => {});
      }
      refreshUnread();

      // Request push notification permission + subscribe
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then(p => { if (p === 'granted') subscribeToPush(); });
      } else if ('Notification' in window && Notification.permission === 'granted') {
        subscribeToPush();
      }

      // SSE for real-time notifications
      const token = getToken();
      if (token && typeof EventSource !== 'undefined') {
        const url = `${API_BASE}/api/notifications/stream`;
        // Use fetch-based SSE since EventSource doesn't support headers
        let aborted = false;
        const ctrl = new AbortController();
        (async () => {
          try {
            const resp = await fetch(url, {
              headers: { 'Authorization': `Bearer ${token}` },
              signal: ctrl.signal,
            });
            const reader = resp.body?.getReader();
            if (!reader) return;
            const decoder = new TextDecoder();
            while (!aborted) {
              const { done, value } = await reader.read();
              if (done) break;
              const text = decoder.decode(value, { stream: true });
              if (text.startsWith('data:')) {
                refreshUnread();
              }
            }
          } catch (e) {
            // SSE connection closed or aborted — normal on nav away
          }
        })();
        sseRef.current = ctrl;
      }

      // Fallback polling every 30s
      const interval = setInterval(refreshUnread, 30000);
      return () => {
        clearInterval(interval);
        if (sseRef.current) { sseRef.current.abort(); sseRef.current = null; }
      };
    }
  }, [user, isSuperAdmin, refreshUnread]);

  return (
    <div className="flex min-h-screen bg-[hsl(var(--background))] transition-colors duration-200">
      {/* Accessibility: Skip to content */}
      <a href="#main-content" className="skip-link" data-testid="skip-link">Skip to content</a>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar - hidden on mobile, visible on desktop */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      <main className="flex-1 flex flex-col min-w-0">
        <ReadonlyBanner />
        {/* Glassmorphism top bar */}
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 dark:bg-[hsl(260,15%,8%,0.7)] border-b border-purple-100 dark:border-purple-900/30 px-4 sm:px-6 py-2 flex items-center gap-3">
          {/* Mobile hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
            data-testid="mobile-menu-btn"
            aria-label="Toggle sidebar menu"
          >
            {sidebarOpen ? <X className="w-4 h-4 text-purple-500" /> : <Menu className="w-4 h-4 text-purple-500" />}
          </button>

          {/* Branding — SuperAdmin sees Transsolution; clients see their own company */}
          {isSuperAdmin ? (
            <div className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-purple-700 dark:text-purple-300 min-w-0 shrink-0" data-testid="header-platform-brand" title="Transsolution — Platform Admin">
              <Shield className="w-3.5 h-3.5 shrink-0" />
              <span>TRANSSOLUTION</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-600 text-white rounded">PLATFORM</span>
            </div>
          ) : (
            companyName && (
              <div className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-purple-700 dark:text-purple-300 min-w-0 shrink-0 max-w-[320px]" data-testid="header-company-name" title={companyName}>
                <Building2 className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{companyName}</span>
              </div>
            )
          )}

          <div className="flex-1 max-w-md mx-auto">
            <GlobalSearch />
          </div>

          {/* Notification Bell */}
          <button
            className="relative p-2 rounded-lg border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
            data-testid="notification-bell"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            title="Notifications"
          >
            <Bell className="w-4 h-4 text-purple-400" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="p-2 rounded-lg border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-purple-400" />}
          </button>
        </div>
        <div id="main-content" className="flex-1 px-3 sm:px-6 py-4 sm:py-5 pb-20 lg:pb-5" role="main" aria-label="Main content">
          {children || <Outlet />}
        </div>
      </main>
      {user && <MobileBottomNav />}
      {user && <ReportIssueButton />}
    </div>
  );
}
